import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router-dom";
import {
  Carousel,
  ButtonIcon,
  Icon,
  ContentAlignment,
  Link
} from "fr-ito-web-react/react/esm";
import { NewImage } from "@common/index";
import { Caption } from "@common/DynamicUikit";
import {
  COUNTRY,
  RouterUrlType,
  StylingType,
  UserListImagesType,
  UserListOutfitsType
} from "@type/index";
import { footAndInches, userHeight } from "@util/height";
import { isPCSize } from "@util/helpers";
import { ImageComponentNames, optimizedUGCImageUrlByDevice } from "@util/image";
import { trackClickRecommendUserEvent } from "@util/analytics/utils";
import styles from "./styles.module.css";

type Props = {
  gender: string;
  listData: Array<UserListImagesType>;
  recommendClick: (id: number) => void;
  country: COUNTRY;
};

const RecommendUser = (props: Props) => {
  const { gender, listData, recommendClick, country } = props;
  const { t } = useTranslation();
  const params = useParams();
  const recommendList = listData.slice(0, 20);

  const recommendInfo = (item: UserListOutfitsType[]) => {
    const realItems = item.slice(0, 4);
    return realItems;
  };

  // user page url
  const userPageUrl = (user_id: number | undefined): string | undefined => {
    if (!user_id) {
      return undefined;
    } else {
      const url = generatePath(
        `/:region/:language/stylingbook/${StylingType.stylehint}/user/${user_id}`,
        {
          ...params
        } as RouterUrlType
      );
      return url;
    }
  };

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    userId: number | undefined,
    index: number
  ) => {
    e.preventDefault();
    if (userId) {
      // new ga event - click_recommend_user
      trackClickRecommendUserEvent({
        style_user_id: userId.toString(),
        style_user_index: index + 1
      });
      recommendClick(userId);
    }
  };

  const getRecommendCss = (count: number | undefined, index: number) => {
    if (count === 1) {
      return styles.ItemCount1;
    }
    if (count === 2) {
      return styles.ItemCount2;
    }
    if (count === 3 && index === 0) {
      return styles.ItemCount3;
    }
    return undefined;
  };

  return (
    <div className={styles.RecommendUser}>
      <Caption element="p" color="primary-dark" className={styles.Title}>
        {t("text_styling_book_stylehint_recommend_user")}
      </Caption>
      <div className={styles.Carousel}>
        <Carousel
          tabIndex={0}
          activeIndex={0}
          leftCarouselIcon={
            isPCSize() ? (
              <ButtonIcon
                aria-label="arrow left"
                element="button"
                icon={<Icon name="doubleArrowLeft" />}
                tabIndex={-1}
              />
            ) : undefined
          }
          rightCarouselIcon={
            isPCSize() ? (
              <ButtonIcon
                aria-label="arrow right"
                element="button"
                icon={<Icon name="doubleArrowRight" />}
                tabIndex={-1}
              />
            ) : undefined
          }
          swiperOptions={{
            a11y: {
              nextSlideMessage: "Next slide",
              prevSlideMessage: "Previous slide"
            },
            breakpoints: {
              "320": {
                slidesPerView: 1.4
              },
              "600": {
                slidesPerView: 2.8
              },
              "960": {
                slidesPerView: 4.6
              }
            },
            cssMode: undefined
          }}
        >
          {recommendList.map((list: UserListImagesType, listIndex: number) => (
            <div className={styles.Container}>
              <Link
                className={styles.ContainerLink}
                element="a"
                href={userPageUrl(list.id_ugc_author_data) || ""}
                target="_self"
                onClick={(
                  e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
                ) => handleClick(e, list.id_ugc_author_data, listIndex)}
              >
                <div className={styles.Grid}>
                  {recommendInfo(list.outfits || []).map(
                    (item: UserListOutfitsType, index: number) => {
                      if (item === null) {
                        return (
                          <div className={styles.ItemCommon}>
                            <div className={styles.ImagePlaceholder} />
                          </div>
                        );
                      }
                      return (
                        <div
                          className={getRecommendCss(
                            list.outfits?.length,
                            index
                          )}
                        >
                          <NewImage
                            ratio="3x4"
                            alt={item.id_ugc_dist_content.toString()}
                            src={optimizedUGCImageUrlByDevice(
                              item.image_original || "",
                              ImageComponentNames.recommendStylingImage
                            )}
                          />
                        </div>
                      );
                    }
                  )}
                </div>
                <ContentAlignment
                  alignment={"center"}
                  justifyContent={"space-between"}
                  className={styles.User}
                >
                  <ContentAlignment alignment={"center"} spacing={"12"}>
                    <div className={styles.ImageAvatar}>
                      <NewImage
                        width={"36px"}
                        height={"36px"}
                        ratio="1x1"
                        src={optimizedUGCImageUrlByDevice(
                          list.picture_url || "",
                          ImageComponentNames.stylingUserSmallImage
                        )}
                        designTokens={{
                          borderRadiusToken: "ito-border-radius-50-percent"
                        }}
                      />
                    </div>
                    <ContentAlignment
                      direction={"row"}
                      spacing={"4"}
                      alignment={"center"}
                    >
                      <Caption
                        element="p"
                        color="primary-dark"
                        className={styles.UserName}
                      >
                        {list.nick_name}
                      </Caption>
                      {/* is staff */}
                      {list.user_type === 2 && (
                        <Icon
                          className={styles.Badge}
                          name="verifiedBadge"
                          size={12}
                          fill
                        />
                      )}
                    </ContentAlignment>
                  </ContentAlignment>
                  <Caption element="p" className={styles.UserHeight}>
                    {`${userHeight(
                      footAndInches[country]
                        ? list.user_height_inch || 0
                        : list.user_height || 0
                    )}${t("text_style_model_height")}`}
                  </Caption>
                </ContentAlignment>
              </Link>
            </div>
          ))}
        </Carousel>
      </div>
      <Link
        className={styles.MoreLink}
        element="a"
        href={generatePath(
          `/:region/:language/stylingbook/${StylingType.stylehint}/userlist/${gender}`,
          {
            ...params
          } as RouterUrlType
        )}
      >
        <ContentAlignment direction={"row-reverse"}>
          <Caption element="p" className={styles.MoreText}>
            {t("text_styling_book_stylehint_recommend_user_view_more")}
          </Caption>
        </ContentAlignment>
      </Link>
    </div>
  );
};

export default RecommendUser;
